import React from 'react';
import { Shield, Database, Cookie, Lock, Eye, UserX } from 'lucide-react';

const PrivacyPolicyPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="mb-6 text-blue-600 hover:text-blue-800 flex items-center gap-2"
        >
          ← Back to Home
        </button>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
          </div>

          <p className="text-gray-700 mb-8">
            At AbleFam, we take your privacy seriously. This policy describes how we collect, 
            use, and protect your personal information.
          </p>

          {/* Information We Collect */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Information We Collect</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect information you provide directly to us, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Account information (name, email address)</li>
                <li>Profile information you choose to provide</li>
                <li>Content you submit (venue reviews, community posts, venue suggestions)</li>
                <li>Communications you send to us</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We automatically collect certain information when you use our services, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Usage information (pages visited, features used)</li>
                <li>Device information (browser type, operating system)</li>
                <li>Location information (with your permission, for venue searches)</li>
              </ul>
            </div>
          </section>

          {/* How We Use Information */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">How We Use Your Information</h2>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Provide, maintain, and improve our services</li>
              <li>Process and display your contributions (reviews, venue submissions)</li>
              <li>Send you updates and notifications you've requested</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent or unauthorized activities</li>
            </ul>
          </section>

          {/* Information Sharing */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <UserX className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Information Sharing</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell your personal information. We may share information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>With your consent or at your direction</li>
              <li>In response to legal process or government requests</li>
              <li>To protect our rights, privacy, safety, or property</li>
              <li>In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Public contributions (venue reviews, community posts) are visible to other users 
              and may be displayed with your username.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Cookie className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Cookies & Tracking</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We use cookies and similar technologies to maintain your session, remember your 
              preferences, and understand how you use our services. You can control cookies 
              through your browser settings.
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Data Security</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We use industry-standard security measures to protect your information, including 
              encryption, secure servers, and access controls. However, no method of transmission 
              over the Internet is 100% secure.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">You may:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Access, update, or delete your account information</li>
              <li>Opt out of marketing communications</li>
              <li>Request a copy of your data</li>
              <li>Request deletion of your data (subject to legal requirements)</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              AbleFam is intended for use by adults (parents, caregivers, and family members). 
              We do not knowingly collect personal information from children under 13. If you 
              believe we have collected information from a child, please contact us.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions about this Privacy Policy, please contact us through our 
              Contact page.
            </p>
          </section>

          {/* Last Updated */}
          <div className="text-sm text-gray-500 pt-6 border-t border-gray-200">
            Last updated: January 2025
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
